import React from "react";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import DoneRoundedIcon from "@material-ui/icons/DoneRounded";
import { TableSelectMultiProps } from "./TableSelectMulti.types";
import { Input, SvgIcon } from "@material-ui/core";
import { MuiThemeProvider } from "@material-ui/core/styles";
import {
  MenuProps,
  useStyles,
  Title,
  SelectContainer,
  MuiTheme,
} from "./TableSelectMulti.styles";

export const TableSelectMulti: React.FC<TableSelectMultiProps> = ({
  options,
  onFilterChanged,
  id,
  title,
}) => {
  const classes = useStyles();

  const cultureIndex = [
    {
      category: "Visionary",
      subCategories: ["Daredevil", "Philosopher", "Trailblazer", "Architect"],
    },
    {
      category: "Research",
      subCategories: ["Scholar", "Specialist", "Craftsman", "Technical Expert"],
    },
    {
      category: "Social",
      subCategories: [
        "Persuader",
        "Rainmaker",
        "Influencer",
        "Debater",
        "Socializer",
      ],
    },
    {
      category: "Organizational",
      subCategories: [
        "Administrator",
        "Coordinator",
        "Facilitator",
        "Operator",
        "Traditionalist",
      ],
    },
  ];
  const categories = cultureIndex.map(item => item.category);
  const [selections, setSelections] = React.useState<string[]>([
    ...options,
    ...categories,
  ]);

  const handleChange = (event: React.ChangeEvent<{ value: any }>) => {
    const value = event.target.value;
    const selection = value[value.length - 1];
    let newSelection: string[] = [];

    if (selection === "any") {
      if (selections.length === options.length + categories.length) {
        setSelections(newSelection);
      } else {
        newSelection = [...options, ...categories];
        setSelections(newSelection);
      }

      // if selection is a category
    } else if (categories.indexOf(selection) > -1) {
      // if category already selected
      if (selections.indexOf(selection) > -1) {
        // remove category and its subcategories
        newSelection = selections.filter(item => {
          const subcatIdx = categories.indexOf(selection);
          return (
            cultureIndex[subcatIdx].subCategories.indexOf(item) < 0 &&
            item !== selection
          );
        });
        setSelections(newSelection);

        // if category not already selected
      } else {
        // add category and all subcategories that haven't been selected
        newSelection = [...selections, selection];
        const catIndex = categories.indexOf(selection);
        cultureIndex[catIndex].subCategories.forEach(subCat => {
          if (selections.indexOf(subCat) < 0) newSelection.push(subCat);
        });
        setSelections(newSelection);
      }

      // if selection is a subcategory
    } else {
      // find the subcategory's category
      let category = "";
      let subCategories: string[] = [];
      for (const cat of cultureIndex) {
        if (cat.subCategories.indexOf(selection) > -1) {
          category = cat.category;
          subCategories = cat.subCategories;
          break;
        }
      }

      // if the subcat is already selected
      if (selections.indexOf(selection) > -1) {
        // remove subcategory
        newSelection = selections.filter(item => item !== selection);
        // check if we need to deselect the category
        if (subCategories.every(subCat => newSelection.indexOf(subCat) < 0)) {
          // deselect category and subcategory
          setSelections(newSelection.filter(item => item !== category));
        } else {
          // deselect subcategory only
          setSelections(newSelection);
        }

        // if the subcat is not already selected
      } else {
        // add subcategory
        newSelection = [...selections, selection];
        // check if we need to select the category as well
        if (newSelection.indexOf(category) > -1) {
          // select subcategory only
          setSelections(newSelection);
        } else {
          // select subcategory and category
          setSelections([...newSelection, category]);
        }
      }
    }

    onFilterChanged(id, newSelection);
  };

  const constructArray = (data: any) => {
    const items = [];

    for (const cat of data) {
      items.push(
        <MenuItem
          selected
          key={cat.category}
          value={cat.category}
          classes={{
            root:
              selections.indexOf(cat.category) > -1
                ? classes.selected
                : classes.root,
          }}
        >
          <DoneRoundedIcon
            style={{
              fontSize: "16px",
              marginLeft: "-10px",
              marginRight: "5px",
            }}
          />
          {cat.category}
        </MenuItem>
      );
      for (const subCategory of cat.subCategories) {
        items.push(
          <MenuItem
            selected
            key={subCategory}
            value={subCategory}
            classes={{
              root:
                selections.indexOf(subCategory) > -1
                  ? classes.selectedsubCat
                  : classes.rootsubCat,
            }}
          >
            <DoneRoundedIcon
              style={{
                fontSize: "16px",
                marginLeft: "-10px",
                marginRight: "5px",
              }}
            />
            {subCategory}
          </MenuItem>
        );
      }
    }

    return items;
  };

  return (
    <SelectContainer>
      <Title>{title}</Title>
      <FormControl className={classes.formControl}>
        <MuiThemeProvider theme={MuiTheme}>
          <Select
            multiple
            value={cultureIndex}
            onChange={handleChange}
            input={
              <Input
                classes={{
                  formControl: classes.inputFormControl,
                  root: classes.inputRoot,
                  input: classes.input,
                }}
              />
            }
            MenuProps={MenuProps}
            renderValue={() => {
              if (selections.length === options.length + categories.length) {
                return "Any " + title;
              } else {
                return selections.join(", ");
              }
            }}
            onClose={() => {
              if (selections.length === 0) {
                setSelections([...options, ...categories]);
                onFilterChanged(id, ["any"]);
              }
            }}
            IconComponent={props => (
              <SvgIcon {...props}>
                <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
              </SvgIcon>
            )}
          >
            <MenuItem
              selected
              value={"any"}
              classes={{
                root:
                  selections.length === options.length + categories.length
                    ? classes.selectedAny
                    : classes.rootAny,
              }}
            >
              {"Any " + title}
            </MenuItem>
            {constructArray(cultureIndex)}
          </Select>
        </MuiThemeProvider>
      </FormControl>
    </SelectContainer>
  );
};
